import { useMemo } from "react";
import { useWatch } from "react-hook-form";
import currency from "currency.js";
import type { FindPackageByIdData } from "@/app/lib/types/codegen";
import type { BookingData, TravellerType } from "./types";

type TravellerPriceMap = Record<TravellerType, number>;

export function useTripPrice(tripData: FindPackageByIdData) {
  const selectedTravellers = useWatch<BookingData, "travellers">({
    name: "travellers",
  });

  return useMemo(() => {
    const prices: TravellerPriceMap = {
      adultInDoubleRoom: tripData.priceForAdultInADoubleRoom || 0,
      adultInSingleRoom: tripData.priceForAdultInASingleRoom || 0,
      childWithBed: tripData.priceForChildWithBed || 0,
      childWithoutBed: tripData.priceForChildWithoutBed || 0,
      infant: tripData.priceForInfant || 0,
    };

    const discountedPrices: TravellerPriceMap = {
      adultInDoubleRoom: tripData.discountedPriceForAdultInADoubleRoom || 0,
      adultInSingleRoom: tripData.discountedPriceForAdultInASingleRoom || 0,
      childWithBed: tripData.discountedPriceForChildWithBed || 0,
      childWithoutBed: tripData.discountedPriceForChildWithoutBed || 0,
      infant: tripData.discountedPriceForInfant || 0,
    };

    const { totalPrice, totalDiscountedPrice } = Object.entries(
      selectedTravellers,
    ).reduce(
      (acc, [travellerType, count]) => {
        const type = travellerType as TravellerType;

        return {
          totalPrice: currency(prices[type], { precision: 3 })
            .multiply(count)
            .add(acc.totalPrice).value,
          totalDiscountedPrice: currency(discountedPrices[type], {
            precision: 3,
          })
            .multiply(count)
            .add(acc.totalDiscountedPrice).value,
        };
      },
      { totalPrice: 0, totalDiscountedPrice: 0 },
    );

    return {
      totalPrice,
      totalDiscountedPrice,
      totalDiscount: currency(totalPrice, { precision: 3 }).subtract(
        totalDiscountedPrice,
      ).value,
    };
  }, [selectedTravellers, tripData]);
}
